import { Button } from '@factorialco/deprecated-design-system'
import { SecuritySettingsDomainSectionFragment } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useCreateAuthenticationDomain } from '../useCreateAuthenticationDomain'
import { DOMAIN_REGEX } from './constants'

interface AddDomainFormValues {
  domainName: string
}

interface Props {
  onClose: () => void
  onSuccess: (newDomain: SecuritySettingsDomainSectionFragment) => void
}

export const AddDomainModal = ({ onClose, onSuccess }: Props) => {
  const { createAuthenticationDomain, loading: creating } = useCreateAuthenticationDomain()

  const form = useForm<AddDomainFormValues>({
    defaultValues: {
      domainName: '',
    },
    validate: (data) => {
      const errors: FormErrors<AddDomainFormValues> = {}
      if (!DOMAIN_REGEX.test(data.domainName)) {
        errors.domainName = i18n.t('security_settings.domain.create.validation_error')
      }
      return errors
    },
    onSubmit: async (data) => {
      const result = await createAuthenticationDomain(data.domainName)

      const handled = handleResult(result, (formErrors) => {
        app.setFlash({
          type: 'wrong',
          message: formErrors.errors || i18n.t('security_settings.domain.create.error'),
          time: 60000,
        })
        form.setErrors({ domainName: formErrors.domainName })
      })

      if (handled.success) {
        const domain = handled.data.domain
        if (!domain) return handled

        app.setFlash({
          type: 'correct',
          message: i18n.t('security_settings.domain.create.success'),
        })
        onClose()
        onSuccess(domain)
      }
      return handled
    },
  })

  return (
    <Modal
      illustration="server-edit"
      title={i18n.t('security_settings.domain.create.title')}
      description={i18n.t('security_settings.domain.create.description')}
      onSubmit={() => form.handleSubmit()}
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={!form.dirty || creating} submit>
            {i18n.t('security_settings.domain.create.submit')}
          </Button>
        </>
      }
    >
      <TextField
        label={i18n.t('security_settings.domain.create.label')}
        value={form.values.domainName}
        onChange={(value) => form.setValues({ ...form.values, domainName: value })}
        error={form.touched.domainName ? form.errors.domainName : undefined}
        readonly={creating}
      />
    </Modal>
  )
}
